import styled from '@emotion/styled'
import {
  arrow,
  autoUpdate,
  FloatingArrow,
  FloatingPortal,
  flip,
  offset,
  type Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react'
import React, {
  type CSSProperties,
  type ComponentProps,
  cloneElement,
  isValidElement,
  memo,
  type ReactElement,
  type ReactNode,
  useRef,
  useState,
} from 'react'

import { useHasHoverCapability } from '../../code-only/hooks/index.ts'
import { tooltipZIndex } from '../../code-only/z-indexes.tsx'
import { borderRadius, colorTokens, spacing } from '../../foundations/index.ts'
import { Text } from '../../foundations/text/index.ts'

type ArrowVerticalPosition = 'top' | 'middle' | 'bottom'
type ArrowHorizontalPosition = 'left' | 'middle' | 'right'

const ARROW_HEIGHT = 6
const ARROW_WIDTH = 12
const GAP = 6
const FADE_DURATION_MS = 150

function isInlineStyleObject(style: unknown): style is CSSProperties {
  return typeof style === 'object' && style !== null && !Array.isArray(style)
}

export type TooltipProps = {
  title: ReactNode
  children: ReactNode
  arrowHorizontalPosition?: ArrowHorizontalPosition | undefined
  arrowVerticalPosition?: ArrowVerticalPosition | undefined
  showArrow?: boolean | undefined
  className?: string | undefined
  cursor?: string | undefined
  disabled?: boolean | undefined
  /**
   * On touch-only devices, hover doesn't reliably exist, so the tooltip is triggered by a
   * press/click instead. Devices that can hover (mouse, trackpad) keep the default hover
   * behavior. Opt-in to avoid changing the many tooltips that wrap clickable elements
   * (buttons, icons with their own onClick).
   */
  isClickable?: boolean | undefined
}

export const Tooltip = memo<TooltipProps>(function Tooltip(props: TooltipProps) {
  const {
    title,
    children,
    arrowHorizontalPosition = 'middle',
    arrowVerticalPosition = 'middle',
    showArrow = true,
    className,
    cursor,
    disabled,
    isClickable,
    ...otherProps
  } = props
  const hasHover = useHasHoverCapability()
  const arrowRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: floatingUIArrowPosition[arrowVerticalPosition][arrowHorizontalPosition],
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift(),
      // eslint-disable-next-line react-hooks/refs
      arrow({
        element: showArrow ? arrowRef : null,
      }),
      offset(showArrow ? ARROW_HEIGHT + GAP : GAP),
    ],
  })

  // Event listeners to change the open state
  const hover = useHover(context, { move: false, enabled: !isClickable || hasHover })
  const click = useClick(context, { enabled: !!isClickable && !hasHover })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  // Role props for screen readers
  const role = useRole(context, { role: 'tooltip' })

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, click, focus, dismiss, role])

  const { isMounted, status } = useTransitionStatus(context, { duration: FADE_DURATION_MS })

  const child = React.Children.only(children) // Assuming only one child
  const childStyleProp = isValidElement<ComponentProps<typeof ChildrenContainer>>(child) ? child.props.style : undefined
  const childStyles = isInlineStyleObject(childStyleProp) ? childStyleProp : undefined
  const childClassName = isValidElement<ComponentProps<typeof ChildrenContainer>>(child) ? child.props.className : '' // Get child className
  const childCss = isValidElement<ComponentProps<typeof ChildrenContainer>>(child) ? child.props.css : {} // Get child css

  if (disabled) {
    return children
  }

  return (
    <>
      <ChildrenContainer
        ref={refs.setReference}
        {...getReferenceProps()}
        style={childStyles}
        className={childClassName}
        css={childCss}
        cursor={cursor}
      >
        {isValidElement(children)
          ? cloneElement(children as ReactElement, {
              ...otherProps,
            })
          : children}
      </ChildrenContainer>

      {isMounted && title ? (
        <FloatingPortal>
          <TooltipContainer
            // eslint-disable-next-line react-hooks/refs
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={className}
            status={status}
          >
            <Text variant="body2">{title}</Text>
            {showArrow ? (
              <FloatingArrow
                ref={arrowRef}
                context={context}
                width={ARROW_WIDTH}
                height={ARROW_HEIGHT}
                fill={colorTokens['color-bg-base-inverse']}
              />
            ) : null}
          </TooltipContainer>
        </FloatingPortal>
      ) : null}
    </>
  )
})
const ChildrenContainer = styled.div<{ cursor?: string | undefined }>`
  display: inherit;
  min-width: 0;
  max-width: 100%;
  cursor: ${({ cursor }) => cursor ?? 'pointer'};
`

type TransitionStatus = ReturnType<typeof useTransitionStatus>['status']

const TooltipContainer = styled.div<{ status: TransitionStatus }>`
  position: absolute;
  z-index: ${tooltipZIndex};
  display: inline-flex;
  justify-content: center;
  align-items: center;

  min-width: 0;
  max-width: 240px;
  white-space: pre-line;

  padding: ${spacing[20]} ${spacing[30]};
  border-radius: ${borderRadius['20']};

  color: ${colorTokens['color-text-base-primary']};
  background: ${colorTokens['color-bg-base-inverse']};

  animation: ${({ status }) => (status === 'close' ? 'tooltip-fade-out' : 'tooltip-fade-in')} ${FADE_DURATION_MS}ms
    ${({ status }) => (status === 'close' ? 'ease-in' : 'ease-out')} forwards;

  @keyframes tooltip-fade-in {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  @keyframes tooltip-fade-out {
    from {
      opacity: 1;
    }

    to {
      opacity: 0;
    }
  }
`

type FloatingUIArrowPositionByArrowPosition = {
  [key in ArrowVerticalPosition]: { [key in ArrowHorizontalPosition]: Placement }
}

const floatingUIArrowPosition: FloatingUIArrowPositionByArrowPosition = {
  top: {
    left: 'bottom-start',
    middle: 'bottom',
    right: 'bottom-end',
  },
  middle: {
    left: 'right',
    middle: 'bottom',
    right: 'left',
  },
  bottom: {
    left: 'top-start',
    middle: 'top',
    right: 'top-end',
  },
}
